import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Handshake, Search, Filter, Plus, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { getPartnerships, deletePartnership, updatePartnership } from '@/lib/firebase';
import AddPartnershipModal from '@/components/add-partnership-modal';
import EditPartnershipModal from '@/components/edit-partnership-modal';
import { logger } from '@/lib/logger';

interface Partnership {
  id: string;
  name: string;
  type: string;
  logoUrl: string;
  enabled: boolean;
  created?: any;
}

export default function Partnerships() {
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [filteredPartnerships, setFilteredPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const typeOptions = ['all', 'Partner', 'Reseller'];

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        logger.log('🤝 Loading partnerships...');
        const data = await getPartnerships();
        setPartnerships(data as Partnership[]);
        logger.log('📋 Partnerships loaded:', data.length);
      } catch (error: any) {
        logger.error('❌ Error loading partnerships:', error);
        toast({ title: "Failed to load partnerships", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  useEffect(() => {
    let filtered = partnerships;

    if (searchTerm.trim()) {
      filtered = filtered.filter(partnership =>
        partnership.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(partnership => partnership.type === typeFilter);
    }

    setFilteredPartnerships(filtered);
  }, [partnerships, searchTerm, typeFilter]);

  const handleDeletePartnership = async (partnershipId: string, partnershipName: string) => {
    if (!confirm(`Are you sure you want to delete "${partnershipName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deletePartnership(partnershipId);
      toast({
        title: "Partnership deleted",
        description: `${partnershipName} has been deleted successfully.`,
      });
    } catch (error: any) {
      logger.error('Error deleting partnership:', error);
      toast({
        title: "Failed to delete partnership",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEditPartnership = (partnership: Partnership) => {
    setSelectedPartnership(partnership);
    setEditModalOpen(true);
  };

  const handleToggleEnabled = async (partnership: Partnership) => {
    try {
      await updatePartnership(partnership.id, { enabled: !partnership.enabled });
      toast({
        title: partnership.enabled ? "Partnership disabled" : "Partnership enabled",
        description: `${partnership.name} has been ${partnership.enabled ? 'disabled' : 'enabled'}.`,
      });
    } catch (error: any) {
      logger.error('Error toggling partnership:', error);
      toast({
        title: "Failed to update partnership",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'partner':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reseller':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading partnerships...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Handshake className="h-6 w-6" />
              Partnerships
            </h1>
            <p className="text-slate-600 mt-1">
              Manage your partner and reseller relationships
            </p>
          </div>
          <Button 
            className="flex items-center gap-2"
            onClick={() => setAddModalOpen(true)}
            data-testid="button-add-partnership"
          >
            <Plus className="h-4 w-4" />
            Add Partnership
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search & Filter</CardTitle>
            <CardDescription>
              Find partnerships by name and filter by type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search by partnership name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-partnerships"
                  />
                </div>
              </div>
              <div className="w-full sm:w-48">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="select-type-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type === 'all' ? 'All Types' : type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Partnerships ({filteredPartnerships.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPartnerships.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {partnerships.length === 0
                  ? "No partnerships found. Click 'Add Partnership' to create one."
                  : "No partnerships match your search criteria."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartnerships.map((partnership) => (
                    <TableRow key={partnership.id}>
                      <TableCell>
                        {partnership.logoUrl ? (
                          <img 
                            src={partnership.logoUrl} 
                            alt={partnership.name} 
                            className="w-10 h-10 object-contain rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                            <Handshake className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{partnership.name}</TableCell>
                      <TableCell>
                        <Badge className={getTypeColor(partnership.type)}>
                          {partnership.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={partnership.enabled ? "default" : "secondary"}
                          className="cursor-pointer"
                          onClick={() => handleToggleEnabled(partnership)}
                        >
                          {partnership.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPartnership(partnership)}
                            data-testid={`button-edit-partnership-${partnership.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePartnership(partnership.id, partnership.name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`button-delete-partnership-${partnership.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <AddPartnershipModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
      />

      {selectedPartnership && (
        <EditPartnershipModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedPartnership(null);
          }}
          partnership={selectedPartnership}
        />
      )}
    </div>
  );
}
